/**
 * /tools/539-combo-calculator — 539 碰數計算機（連碰、立柱、天二、天三、拖膽）
 *
 * SEO 角色：本地術語覆蓋（藍海關鍵字）
 * 目標關鍵字：539 連碰、539 立柱、539 天二、539 天三、539 拖膽、539 碰數計算機
 * 玩法解釋：
 *   - 二/三/四星連碰：C(N, K)
 *   - 立柱（柱碰）：1 膽 + C(M, star-1)
 *   - 天二（兩星包牌）：C(N, 2)
 *   - 天三（三星包牌）：C(N, 3)
 *   - 拖膽：M 膽 + C(N, star-M)
 */

import { useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Layers,
  Calculator as CalculatorIcon,
  AlertTriangle,
  Info,
  ChevronRight,
  BookOpen,
  RotateCcw,
} from 'lucide-react';
import SEOHead from '../../../seo/SEOHead';
import { buildBreadcrumb } from '../../../seo/buildBreadcrumb';
import { trackEvent } from '../../../utils/tracking';


const TOOL_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: '539 碰數計算機',
  url: 'https://cyxey.uk/tools/539-combo-calculator',
  description:
    '539 碰數計算機：連碰、立柱、天二、天三、拖膽 5 種地下盤玩法的組數、總成本、單注獎金與損益平衡點一鍵計算。',
  applicationCategory: 'UtilityApplication',
  applicationSubCategory: '539 彩券工具',
  operatingSystem: 'All',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'TWD' },
  inLanguage: 'zh-TW',
  isAccessibleForFree: true,
};

const BREADCRUMB_SCHEMA = buildBreadcrumb([
  { name: '首頁', path: '/' },
  { name: '工具中心', path: '/tools' },
  { name: '539 碰數計算機', path: '/tools/539-combo-calculator' },
]);

/* ------------------------------------------------------------------ */
/*  Math                                                               */
/* ------------------------------------------------------------------ */

function combinations(n: number, k: number): number {
  if (k < 0 || k > n) return 0;
  if (k === 0 || k === n) return 1;
  k = Math.min(k, n - k);
  let result = 1;
  for (let i = 0; i < k; i++) {
    result = (result * (n - i)) / (i + 1);
  }
  return Math.round(result);
}

/* ------------------------------------------------------------------ */
/*  Play Types                                                         */
/* ------------------------------------------------------------------ */

type PlayType = 'lian2' | 'lian3' | 'lian4' | 'lizhu' | 'tian2' | 'tian3' | 'tuodan';

interface PlayDef {
  key: PlayType;
  label: string;
  star: number;
  needsDan: boolean;
  /** 預設地下盤賠率倍數 */
  defaultOdds: number;
  brief: string;
}

const PLAYS: PlayDef[] = [
  { key: 'lian2', label: '二星連碰', star: 2, needsDan: false, defaultOdds: 53, brief: 'N 顆號碼任選 2 顆組合' },
  { key: 'lian3', label: '三星連碰', star: 3, needsDan: false, defaultOdds: 580, brief: 'N 顆號碼任選 3 顆組合' },
  { key: 'lian4', label: '四星連碰', star: 4, needsDan: false, defaultOdds: 7500, brief: 'N 顆號碼任選 4 顆組合' },
  { key: 'lizhu', label: '立柱（柱碰）', star: 2, needsDan: true, defaultOdds: 53, brief: '指定 1 膽 + 從剩餘任選 K-1 顆' },
  { key: 'tian2', label: '天二（兩星包牌）', star: 2, needsDan: false, defaultOdds: 53, brief: '與二星連碰計算相同' },
  { key: 'tian3', label: '天三（三星包牌）', star: 3, needsDan: false, defaultOdds: 580, brief: '與三星連碰計算相同' },
  { key: 'tuodan', label: '拖膽', star: 3, needsDan: true, defaultOdds: 580, brief: 'M 膽 + 從拖區選 star-M 顆' },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const ALL_NUMBERS = Array.from({ length: 39 }, (_, i) => i + 1);

function ballColor(n: number): string {
  if (n <= 10) return 'bg-red-600';
  if (n <= 20) return 'bg-blue-600';
  if (n <= 30) return 'bg-emerald-600';
  return 'bg-purple-600';
}

export default function Lottery539ComboCalculatorPage() {
  const [playType, setPlayType] = useState<PlayType>('lian2');
  const [picks, setPicks] = useState<Set<number>>(new Set());
  const [dans, setDans] = useState<Set<number>>(new Set());
  const [unitPrice, setUnitPrice] = useState(50);
  const [oddsOverride, setOddsOverride] = useState<number | null>(null);

  const play = PLAYS.find((p) => p.key === playType)!;
  const odds = oddsOverride ?? play.defaultOdds;

  /* ---- handlers ---- */
  const togglePick = useCallback(
    (n: number) => {
      setPicks((prev) => {
        const next = new Set(prev);
        if (next.has(n)) {
          next.delete(n);
          setDans((d) => {
            const nd = new Set(d);
            nd.delete(n);
            return nd;
          });
        } else {
          next.add(n);
        }
        return next;
      });
    },
    [],
  );

  const toggleDan = useCallback(
    (n: number) => {
      setDans((prev) => {
        const next = new Set(prev);
        if (next.has(n)) next.delete(n);
        else next.add(n);
        return next;
      });
    },
    [],
  );

  const handlePlayChange = (p: PlayType) => {
    setPlayType(p);
    setOddsOverride(null);
    if (!PLAYS.find((x) => x.key === p)!.needsDan) setDans(new Set());
    trackEvent('tool_interact', { tool: '539_combo_calculator', action: 'play_change', value: p });
  };

  const handleReset = () => {
    setPicks(new Set());
    setDans(new Set());
    setOddsOverride(null);
    trackEvent('tool_interact', { tool: '539_combo_calculator', action: 'reset' });
  };

  /* ---- calculation ---- */
  const result = useMemo(() => {
    const n = picks.size;
    const m = dans.size;
    const tuoCount = picks.size - dans.size;
    let combos = 0;
    let invalidReason: string | null = null;

    switch (playType) {
      case 'lian2':
      case 'tian2':
        if (n < 2) invalidReason = `需至少選 2 顆號碼（目前 ${n} 顆）`;
        else combos = combinations(n, 2);
        break;
      case 'lian3':
      case 'tian3':
        if (n < 3) invalidReason = `需至少選 3 顆號碼（目前 ${n} 顆）`;
        else combos = combinations(n, 3);
        break;
      case 'lian4':
        if (n < 4) invalidReason = `需至少選 4 顆號碼（目前 ${n} 顆）`;
        else combos = combinations(n, 4);
        break;
      case 'lizhu':
        if (m !== 1) invalidReason = `立柱需指定恰 1 顆膽碼（目前 ${m} 顆膽）`;
        else if (tuoCount < play.star - 1)
          invalidReason = `膽外還需選 ${play.star - 1} 顆拖碼（目前 ${tuoCount} 顆）`;
        else combos = combinations(tuoCount, play.star - 1);
        break;
      case 'tuodan':
        if (m < 1) invalidReason = `拖膽需至少 1 顆膽碼`;
        else if (m >= play.star) invalidReason = `膽碼數量需少於 ${play.star} 顆（目前 ${m} 顆膽）`;
        else if (tuoCount < play.star - m)
          invalidReason = `拖區還需 ${play.star - m} 顆號碼（目前 ${tuoCount} 顆）`;
        else combos = combinations(tuoCount, play.star - m);
        break;
    }

    const totalCost = combos * unitPrice;
    const singlePayout = unitPrice * odds;
    const profitIfOneHit = singlePayout - totalCost;
    const breakEvenHits = totalCost > 0 && singlePayout > 0 ? totalCost / singlePayout : 0;

    return {
      combos,
      totalCost,
      singlePayout,
      profitIfOneHit,
      breakEvenHits,
      invalidReason,
    };
  }, [picks, dans, playType, unitPrice, odds, play.star]);

  return (
    <>
      <SEOHead
        title="連碰計算機｜539碰數・立柱・天地碰一秒算組數金額"
        description="免費 539 連碰計算機（碰數計算機）：二/三/四星連碰、天地碰、立柱（柱碰）、天碰、拖膽 5 種玩法，輸入號碼 1 秒算出碰數組數、總成本、牌支金額與損益平衡點。免登入、手機可用。"
        canonical="https://cyxey.uk/tools/539-combo-calculator"
        jsonLd={[TOOL_SCHEMA, BREADCRUMB_SCHEMA]}
      />

      <div className="min-h-screen bg-[#050505] text-gray-200">
        {/* ── Nav ── */}
        <nav className="max-w-3xl mx-auto px-4 pt-6 pb-2">
          <Link
            to="/tools/539"
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#D4AF37] transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            返回 539 工具集
          </Link>
        </nav>

        <main className="max-w-3xl mx-auto px-4 py-6">
          {/* ── Hero ── */}
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] px-2.5 py-0.5 rounded-full border border-yellow-500/30 text-[#D4AF37] font-medium">
                免費工具
              </span>
              <span className="text-[10px] px-2.5 py-0.5 rounded-full bg-yellow-500/10 text-[#F4C430] font-medium">
                7 種玩法
              </span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-3 flex items-center gap-2.5">
              <Layers className="w-6 h-6 text-[#F4C430]" />
              539 連碰計算機（碰數計算）
            </h1>
            <p className="text-sm text-gray-400 leading-relaxed">
              支援
              <strong className="text-white">連碰、立柱、天二、天三、拖膽</strong>
              5 種地下盤玩法，輸入號碼即時計算
              <strong className="text-white">組數、總成本、單注獎金、損益平衡點</strong>
              。採台灣彩迷慣用術語，地下盤實戰必備。
            </p>
          </header>

          {/* ── Play Type Tabs ── */}
          <section className="mb-6">
            <div className="text-xs text-gray-500 mb-2">選擇玩法</div>
            <div className="flex flex-wrap gap-2">
              {PLAYS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => handlePlayChange(p.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer ${
                    playType === p.key
                      ? 'bg-gradient-to-r from-[#AA6C39] to-[#D4AF37] text-black'
                      : 'border border-yellow-500/30 text-[#D4AF37] hover:bg-yellow-500/10'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-gray-500 mt-2">
              <Info className="w-3 h-3 inline-block mr-1" />
              {play.brief}（適用 <strong className="text-gray-400">{play.star}</strong> 星位）
            </p>
          </section>

          {/* ── Number Grid ── */}
          <section className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs text-gray-500">
                號碼選擇 · 已選 <strong className="text-white">{picks.size}</strong> 顆
                {play.needsDan && (
                  <>
                    {' '}
                    · 膽 <strong className="text-[#F4C430]">{dans.size}</strong> 顆
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={handleReset}
                className="text-[11px] text-gray-400 hover:text-[#F4C430] flex items-center gap-1"
              >
                <RotateCcw className="w-3 h-3" />
                清空
              </button>
            </div>
            <div className="grid grid-cols-10 gap-1.5">
              {ALL_NUMBERS.map((n) => {
                const isPick = picks.has(n);
                const isDan = dans.has(n);
                return (
                  <div key={n} className="flex flex-col items-stretch gap-1">
                    <button
                      type="button"
                      onClick={() => togglePick(n)}
                      className={`aspect-square rounded-full text-xs font-bold transition-all ${
                        isPick
                          ? `${ballColor(n)} text-white shadow-md`
                          : 'bg-[#0a0a0a] border border-gray-800 text-gray-500 hover:border-yellow-500/30'
                      }`}
                    >
                      {n.toString().padStart(2, '0')}
                    </button>
                    {play.needsDan && isPick && (
                      <button
                        type="button"
                        onClick={() => toggleDan(n)}
                        className={`text-[10px] py-0.5 rounded ${
                          isDan
                            ? 'bg-[#F4C430] text-black font-bold'
                            : 'border border-yellow-500/20 text-[#D4AF37] hover:bg-yellow-500/10'
                        }`}
                      >
                        {isDan ? '膽' : '設膽'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* ── Price & Odds Inputs ── */}
          <section className="mb-6 grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-gray-500 mb-1.5 block">每注金額（NT$）</span>
              <input
                type="number"
                min="1"
                value={unitPrice}
                onChange={(e) => setUnitPrice(Math.max(1, Number(e.target.value) || 1))}
                className="w-full px-3 py-2 bg-[#0a0a0a] border border-gray-800 rounded-lg text-sm text-white focus:border-yellow-500/50 outline-none"
              />
            </label>
            <label className="block">
              <span className="text-xs text-gray-500 mb-1.5 block">
                {play.star} 星賠率倍數（預設 {play.defaultOdds}）
              </span>
              <input
                type="number"
                min="1"
                value={oddsOverride ?? play.defaultOdds}
                onChange={(e) =>
                  setOddsOverride(e.target.value ? Math.max(1, Number(e.target.value)) : null)
                }
                className="w-full px-3 py-2 bg-[#0a0a0a] border border-gray-800 rounded-lg text-sm text-white focus:border-yellow-500/50 outline-none"
              />
            </label>
          </section>

          {/* ── Result ── */}
          <section className="mb-10">
            <h2 className="text-base font-bold text-[#F4C430] mb-3 flex items-center gap-2">
              <CalculatorIcon className="w-4 h-4" />
              計算結果
            </h2>
            {result.invalidReason ? (
              <div className="p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/20 text-sm text-[#F4C430]">
                <AlertTriangle className="w-4 h-4 inline-block mr-2" />
                {result.invalidReason}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <ResultCard label="組數（碰數）" value={`${result.combos.toLocaleString()} 組`} />
                <ResultCard label="總成本" value={`NT$ ${result.totalCost.toLocaleString()}`} highlight />
                <ResultCard label="單注獎金" value={`NT$ ${result.singlePayout.toLocaleString()}`} />
                <ResultCard
                  label="若中 1 注盈虧"
                  value={`${result.profitIfOneHit >= 0 ? '+' : ''}NT$ ${result.profitIfOneHit.toLocaleString()}`}
                  positive={result.profitIfOneHit >= 0}
                />
                <ResultCard
                  label="損益平衡點"
                  value={
                    result.breakEvenHits > 0
                      ? `需中 ${result.breakEvenHits.toFixed(2)} 注回本`
                      : 'N/A'
                  }
                  fullWidth
                />
              </div>
            )}

            {/* Quick action: bring numbers to other tools */}
            {picks.size > 0 && !result.invalidReason && (
              <div className="mt-4 flex flex-wrap gap-3">
                <Link
                  to={`/tools/539-payout-calculator?picks=${[...picks].join(',')}`}
                  className="text-xs text-[#F4C430] hover:underline inline-flex items-center gap-1"
                  onClick={() =>
                    trackEvent('539_combo_to_tool_click', { tool: 'payout-calculator' })
                  }
                >
                  → 用此組計算獎金 <ArrowRight className="w-3 h-3" />
                </Link>
                <Link
                  to={`/tools/539-checker?picks=${[...picks].join(',')}`}
                  className="text-xs text-[#F4C430] hover:underline inline-flex items-center gap-1"
                  onClick={() => trackEvent('539_combo_to_tool_click', { tool: 'checker' })}
                >
                  → 開獎後對獎 <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            )}
          </section>

          {/* ── SEO Content ── */}
          <section className="mb-10 p-5 rounded-xl bg-[#0a0a0a] border border-gray-800 space-y-4">
            <h2 className="text-lg font-bold text-[#F4C430] pl-3 border-l-4 border-[#D4AF37]">
              539 碰數玩法完整解釋
            </h2>
            <div className="space-y-3 text-sm text-gray-400 leading-relaxed">
              <div>
                <h3 className="text-base font-bold text-white mb-1.5">什麼是 539 碰數？</h3>
                <p>
                  「碰數」是台灣地下 539 盤口的計算單位，意指「從 N 顆號碼中任選 K 顆的組合數（即 C(N, K)）」。
                  例如選 5 顆號碼跑「二星連碰」，碰數 = C(5, 2) = 10 組，每組獨立下注。
                </p>
              </div>
              <div>
                <h3 className="text-base font-bold text-white mb-1.5">5 種主流玩法對照</h3>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>
                    <strong className="text-white">連碰</strong>
                    （二/三/四星）：所選號碼互相組合，最常見的玩法。
                  </li>
                  <li>
                    <strong className="text-white">立柱（柱碰）</strong>
                    ：指定 1 顆「膽碼」必出，其餘從拖區任選。膽中才有機會中獎。
                  </li>
                  <li>
                    <strong className="text-white">天二 / 天三</strong>
                    ：「兩星 / 三星包牌」的別稱，計算方式與二星 / 三星連碰相同。
                  </li>
                  <li>
                    <strong className="text-white">拖膽</strong>
                    ：M 顆膽碼必出 + 從拖區選剩下號碼，常用於三星玩法。
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-base font-bold text-white mb-1.5">地下盤常見賠率（僅供參考）</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>二星連碰：1 賠 53（即下 50 中 2,650）</li>
                  <li>三星連碰：1 賠 580（即下 50 中 29,000）</li>
                  <li>四星連碰：1 賠 7,500（即下 50 中 375,000）</li>
                </ul>
                <p className="text-[11px] text-gray-500 mt-2">
                  賠率因組頭而異，本工具預設值為市場常見值，實際請以下注盤口為準。
                </p>
              </div>
              <div>
                <h3 className="text-base font-bold text-white mb-1.5">如何用本工具控制成本？</h3>
                <p>
                  選號數量越多碰數成長越快（C(N, K) 為組合數，N=8/star=2 已達 28 組）。
                  建議設定每注金額後，先觀察「總成本」決定是否縮減選號數量；再看「損益平衡點」判斷是否值得下注。
                  立柱玩法因有膽碼鎖定，總成本較低但風險集中於膽中。
                </p>
              </div>
            </div>
          </section>

          {/* ── 速見表 ──
              GSC 2026-08-01~07：32 個「表」意圖查詢（立柱碰數表 184 曝光、立柱連碰表 101、
              539連碰表 41…）合計約 630 曝光全部落在本頁，位置 6-13、合計 1 點擊。
              本頁原本只有計算機與敘述，沒有可直接掃讀的靜態表 —— 這一段就是補那個缺口。 */}
          <section className="mb-10" id="combo-table">
            <h2 className="text-lg font-bold text-[#F4C430] mb-4 pl-3 border-l-4 border-[#D4AF37]">
              539 連碰・立柱碰數速見表（牌支對照）
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-[#1e40af] text-white">
                    <th className="px-3 py-2 whitespace-nowrap">選號數 N</th>
                    <th className="px-3 py-2 whitespace-nowrap">二星連碰</th>
                    <th className="px-3 py-2 whitespace-nowrap">三星連碰</th>
                    <th className="px-3 py-2 whitespace-nowrap">四星連碰</th>
                    <th className="px-3 py-2 whitespace-nowrap">立柱二星<br />（1 膽 + N-1 拖）</th>
                    <th className="px-3 py-2 whitespace-nowrap">立柱三星<br />（1 膽 + N-1 拖）</th>
                  </tr>
                </thead>
                <tbody>
                  {[4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((n) => (
                    <tr key={n} className="border-b border-gray-800">
                      <td className="px-3 py-2 text-center font-bold text-[#D4AF37]">{n}</td>
                      <td className="px-3 py-2 text-center">{combinations(n, 2)}</td>
                      <td className="px-3 py-2 text-center">{combinations(n, 3)}</td>
                      <td className="px-3 py-2 text-center">{combinations(n, 4)}</td>
                      <td className="px-3 py-2 text-center">{combinations(n - 1, 1)}</td>
                      <td className="px-3 py-2 text-center">{combinations(n - 1, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 space-y-1.5 text-xs text-gray-400 leading-relaxed">
              <p>
                表中數字是<strong className="text-white">牌支數（注數）</strong>。連碰 = 從 N 顆號碼任選 K 顆的組合數 C(N, K)，
                沒有指定必出號碼；立柱（柱碰）= 先鎖 1 顆膽碼，再從剩下 N-1 顆拖碼補齊，所以是 C(N-1, K-1)。
              </p>
              <p>
                <strong className="text-white">總金額 = 牌支數 × 單注金額。</strong>
                以組頭常見的單注 NT$50 計：8 號三星連碰 56 支 = NT$2,800；10 號三星連碰 120 支 = NT$6,000；
                同樣 10 號改跑立柱三星只有 36 支 = NT$1,800。
              </p>
              <p>
                需要天地碰、拖膽或自訂單注金額與賠率，請用本頁上方的計算機直接試算。
              </p>
            </div>
          </section>

          {/* ── FAQ (no FAQPage schema, only UI) ── */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-[#F4C430] mb-4 pl-3 border-l-4 border-[#D4AF37]">
              常見問題
            </h2>
            <div className="space-y-3">
              {[
                {
                  q: '連碰與立柱有什麼差別？',
                  a: '連碰是「N 顆號碼互相組合」，沒有指定必出號碼；立柱是「1 顆膽碼鎖定 + 拖區補齊」，膽碼必須開出才有機會中獎。立柱組數較少、單注成本較低，但風險更集中。',
                },
                {
                  q: '天二天三和連碰有差嗎？',
                  a: '計算方式完全一樣（C(N, 2) 與 C(N, 3)），只是組頭間用語不同。「天二 = 兩星包牌 = 二星連碰」、「天三 = 三星包牌 = 三星連碰」。',
                },
                {
                  q: '為什麼選 5 顆跑三星連碰只有 10 組？',
                  a: 'C(5, 3) = 10。從 5 顆號碼任選 3 顆的組合數為 10 種。組合公式 C(N, K) = N! / (K! × (N-K)!)。',
                },
                {
                  q: '損益平衡點怎麼解讀？',
                  a: '損益平衡 = 總成本 / 單注獎金。例如總成本 3,300 元、單注獎金 2,650 元，損益平衡 ≈ 1.25 注，意思是必須中至少 1.25 注才回本（實際只能整數注）。',
                },
                {
                  q: '本工具支援拖膽嗎？',
                  a: '支援。選擇「拖膽」玩法後，先點擊號碼加入選號，再點該號碼下方「設膽」按鈕指定為膽碼。例如 2 膽 + 5 拖跑三星拖膽，組數 = C(5, 1) = 5 組。',
                },
                {
                  q: '本工具是免費的嗎？',
                  a: '是的，cyxey.uk 所有 539 工具完全免費、不需註冊、不需登入，純粹工具站。',
                },
              ].map((faq, idx) => (
                <details
                  key={idx}
                  className="group bg-[#0a0a0a] border border-gray-800 rounded-xl overflow-hidden"
                >
                  <summary className="flex items-center gap-3 px-4 py-3 cursor-pointer text-sm font-bold text-white hover:text-[#F4C430] transition-colors list-none">
                    <ChevronRight className="w-4 h-4 text-[#D4AF37] flex-shrink-0 transition-transform group-open:rotate-90" />
                    {faq.q}
                  </summary>
                  <div className="px-4 pb-4 pl-11 text-sm text-gray-400 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* ── Related Articles ── */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-[#F4C430] mb-4 flex items-center gap-2 pl-3 border-l-4 border-[#D4AF37]">
              <BookOpen className="w-5 h-5" />
              延伸閱讀（539 攻略）
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                {
                  href: '/blog/539-package-betting-guide',
                  title: '539 包牌完整教學',
                  desc: '連碰、立柱、膽拖技巧詳解，含實戰案例',
                },
                {
                  href: '/blog/539-guide',
                  title: '539 新手完整攻略',
                  desc: '玩法規則、選號技巧、投注策略一篇搞懂',
                },
                {
                  href: '/blog/539-winning-rules',
                  title: '539 中獎規則與獎金表',
                  desc: '二星三星四星中獎規則與獎金一覽',
                },
                {
                  href: '/tools/539-payout-calculator',
                  title: '539 獎金計算器',
                  desc: '地下盤賠率、中獎金額試算',
                },
              ].map((art) => (
                <Link
                  key={art.href}
                  to={art.href}
                  className="block p-4 rounded-lg border border-gray-800 hover:border-yellow-500/30 transition-colors"
                >
                  <h3 className="text-sm font-bold text-white mb-1 hover:text-[#F4C430]">
                    {art.title}
                  </h3>
                  <p className="text-xs text-gray-500">{art.desc}</p>
                </Link>
              ))}
            </div>
          </section>


          {/* ── 教學說明（SEO 長文）── */}
          <section className="mb-10 space-y-6">

            {/* ===== 第一章：什麼是 539 連碰 ===== */}
            <h2 className="text-xl font-bold text-white mt-10 mb-4">什麼是 539 連碰？完整圖解說明</h2>

            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              台灣 539 彩券（又稱「今彩 539」）的基本規則非常直觀：從 01 到 39 共 39 個號碼中選出 5 個號碼，開獎時同樣從 39 球中抽出 5 個。對中的號碼數量越多，獎金越高。官方玩法的二星（對中 2 碼）、三星（對中 3 碼）、四星（對中 4 碼）到頭獎（全中 5 碼），構成了不同的獎級層次。
            </p>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              然而，地下盤口的「<strong className="text-white">連碰</strong>」玩法，與官方單注投注截然不同。連碰的核心概念是：<strong className="text-white">從你選定的一組號碼中，列舉出所有可能的 K 星組合，每個組合獨立算一注</strong>。這種做法利用組合數學（Combinatorics）來「擴大覆蓋面」，減少漏號遺憾——當然，代價是注數成倍增長、投注成本也隨之飆升。
            </p>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              舉個最簡單的例子：假設你看好 <strong className="text-white">03、12、18、25、31、07、22、36</strong> 共 8 個號碼，想跑「二星連碰」。計算方式是從 8 個號碼中任選 2 個的組合數，即 C(8, 2) = 28 注。每注 50 元的話，總成本 = 28 × 50 = <strong className="text-white">1,400 元</strong>。只要開出的 5 個球中有任 2 個落在你選的 8 號裡，你就有至少一注中二星。
            </p>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              連碰按星級分為三種主要類型：
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">二星連碰（天二碰）</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              從 N 個號碼中任選 2 個的所有組合，公式為 C(N, 2)。這是最常見的入門連碰方式，因為二星賠率較低（通常 1 賠 53），組數增長相對可控。選 6 號二星連碰只有 15 注，選 10 號也不過 45 注。
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">三星連碰（天三碰）</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              從 N 個號碼中任選 3 個的所有組合，公式為 C(N, 3)。三星賠率高出許多（通常 1 賠 580），但組數也呈三次方增長。選 8 號三星連碰 = C(8, 3) = 56 注，成本直接翻倍。三星連碰適合「看好一批號碼、但不確定哪三個會開」的場景。
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">四星連碰</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              從 N 個號碼中任選 4 個的所有組合，公式為 C(N, 4)。四星賠率極高（通常 1 賠 7,500），但組數爆炸：選 10 號四星連碰 = C(10, 4) = 210 注，每注 50 元就要 10,500 元。四星連碰是高風險高報酬的極端玩法，只建議資深彩迷在嚴格成本控管下使用。
            </p>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              使用上方的 <Link to="/tools/539-combo-calculator" className="text-[#D4AF37] underline">539連碰計算機</Link> 可以即時試算各種連碰組合的注數與成本，輸入號碼點選即出結果。
            </p>

            {/* ===== 第二章：天地碰計算公式 ===== */}
            <h2 className="text-xl font-bold text-white mt-10 mb-4">天地碰計算公式詳解</h2>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              「天碰」和「地碰」是台灣地下 539 的專有術語，分別代表兩種不同的組合邏輯。理解天地碰算法是精算投注成本的基礎，也是本計算機的核心數學引擎。
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">天碰（全數組合）的數學原理</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              天碰的定義非常直接：<strong className="text-white">從你選定的 N 個號碼中，列出所有可能的 K 星組合</strong>。沒有任何號碼被「鎖定」或「優先」，每個號碼的地位完全平等。這就是純粹的組合數學。
            </p>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              天碰公式（組合數公式）：
            </p>
            <div className="p-4 rounded-lg bg-[#0a0a0a] border border-gray-800 mb-3">
              <p className="text-sm text-white font-mono text-center">C(N, K) = N! / (K! × (N − K)!)</p>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              其中 N 是你選的總號碼數，K 是星級（2 星、3 星或 4 星）。驚嘆號「!」代表階乘，例如 5! = 5 × 4 × 3 × 2 × 1 = 120。
            </p>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              <strong className="text-white">範例計算</strong>：7 號天二碰 = C(7, 2) = 7! / (2! × 5!) = (7 × 6) / (2 × 1) = <strong className="text-white">21 注</strong>。如果每注 50 元，總成本 = 21 × 50 = 1,050 元。
            </p>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              再算一個：9 號天三碰 = C(9, 3) = (9 × 8 × 7) / (3 × 2 × 1) = <strong className="text-white">84 注</strong>。每注 50 元，總成本 = 84 × 50 = 4,200 元。可以看到，從二星跳到三星、號碼多加 2 個，成本就從 1,050 翻到 4,200。
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">地碰（膽碼組合）的計算方式</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              地碰的核心概念是「<strong className="text-white">膽碼</strong>」——你指定 1 到多個號碼為「膽」，這些膽碼<strong className="text-white">必須出現在每一注組合中</strong>。剩下的號碼稱為「拖碼」（或「拖區」），只從拖碼中選取補齊星位的號碼。
            </p>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              地碰根據膽碼數量分為兩種常見形式：
            </p>

            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              <strong className="text-white">立柱（柱碰）</strong>：固定 <strong className="text-white">1 個膽碼</strong>，從剩餘 M 個拖碼中選 (K − 1) 個組合。
            </p>
            <div className="p-4 rounded-lg bg-[#0a0a0a] border border-gray-800 mb-3">
              <p className="text-sm text-white font-mono text-center">立柱組數 = C(M, K − 1)</p>
              <p className="text-xs text-gray-500 text-center mt-1">M = 拖碼數量，K = 星級</p>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              例如：膽碼 = 07，拖碼 = {'{'}12, 18, 25, 31, 36{'}'}（5 個拖碼），跑二星立柱。組數 = C(5, 1) = <strong className="text-white">5 注</strong>。比起 6 號二星天碰的 C(6, 2) = 15 注，立柱少了三分之二的注數——但代價是：如果膽碼 07 沒開出來，你一注都不會中。
            </p>

            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              <strong className="text-white">拖膽</strong>：固定 <strong className="text-white">M 個膽碼</strong>（M &ge; 1 且 M &lt; K），從 N 個拖碼中選 (K − M) 個組合。
            </p>
            <div className="p-4 rounded-lg bg-[#0a0a0a] border border-gray-800 mb-3">
              <p className="text-sm text-white font-mono text-center">拖膽組數 = C(N, K − M)</p>
              <p className="text-xs text-gray-500 text-center mt-1">N = 拖碼數量，K = 星級，M = 膽碼數量</p>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              例如：2 個膽碼 + 5 個拖碼，跑三星拖膽。膽碼 2 個已佔兩個星位，只需從 5 個拖碼中再選 1 個，組數 = C(5, 1) = <strong className="text-white">5 注</strong>。如果改成 1 膽 + 5 拖跑三星拖膽，組數 = C(5, 2) = <strong className="text-white">10 注</strong>。
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">天碰 vs 地碰比較表</h3>
            <div className="overflow-x-auto mb-3">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left py-2 px-3 text-gray-400 font-semibold">比較項目</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-semibold">天碰（全數組合）</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-semibold">地碰（膽碼組合）</th>
                  </tr>
                </thead>
                <tbody className="text-gray-400">
                  <tr className="border-b border-gray-800/50">
                    <td className="py-2 px-3 text-white">膽碼</td>
                    <td className="py-2 px-3">無，號碼地位平等</td>
                    <td className="py-2 px-3">1 個以上，必須出現</td>
                  </tr>
                  <tr className="border-b border-gray-800/50">
                    <td className="py-2 px-3 text-white">組數公式</td>
                    <td className="py-2 px-3 font-mono text-xs">C(N, K)</td>
                    <td className="py-2 px-3 font-mono text-xs">C(拖碼數, K − 膽數)</td>
                  </tr>
                  <tr className="border-b border-gray-800/50">
                    <td className="py-2 px-3 text-white">注數</td>
                    <td className="py-2 px-3">較多（覆蓋全面）</td>
                    <td className="py-2 px-3">較少（成本較低）</td>
                  </tr>
                  <tr className="border-b border-gray-800/50">
                    <td className="py-2 px-3 text-white">風險分佈</td>
                    <td className="py-2 px-3">均勻分散</td>
                    <td className="py-2 px-3">集中於膽碼是否開出</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 text-white">適合場景</td>
                    <td className="py-2 px-3">不確定哪些號碼會出</td>
                    <td className="py-2 px-3">對某幾個號碼信心極高</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              兩種碰法各有優劣，沒有絕對的「好壞」之分。天碰適合「看好一批號碼但拿不準」的情境，地碰適合「有 1-2 個超級看好的號碼」的情境。你可以用本頁上方的 <Link to="/tools/539-combo-calculator" className="text-[#D4AF37] underline">539連碰計算機</Link> 分別試算天碰與地碰的組數和成本，再決定要採用哪種投注策略。
            </p>

            {/* ===== 第三章：投注金額試算 ===== */}
            <h2 className="text-xl font-bold text-white mt-10 mb-4">投注金額試算範例</h2>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              以下以地下盤最常見的 <strong className="text-white">每注 50 元</strong> 為基準，列出幾個典型的投注試算。實際金額請以組頭盤口為準。
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">範例 1：10 號二星天碰</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              選 10 個號碼，跑二星天碰。組數 = C(10, 2) = 45 注。<br />
              總成本 = 45 × 50 = <strong className="text-white">2,250 元</strong>。<br />
              單注獎金（1 賠 53）= 50 × 53 = 2,650 元。<br />
              若中 1 注盈虧 = 2,650 − 2,250 = <strong className="text-emerald-400">+400 元</strong>。<br />
              損益平衡 = 2,250 / 2,650 ≈ 0.85 注（中 1 注就回本且有利潤）。
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">範例 2：8 號三星天碰</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              選 8 個號碼，跑三星天碰。組數 = C(8, 3) = 56 注。<br />
              總成本 = 56 × 50 = <strong className="text-white">2,800 元</strong>。<br />
              單注獎金（1 賠 580）= 50 × 580 = 29,000 元。<br />
              若中 1 注盈虧 = 29,000 − 2,800 = <strong className="text-emerald-400">+26,200 元</strong>。<br />
              損益平衡 = 2,800 / 29,000 ≈ 0.10 注。三星高賠率的優勢在此展現：只要中一注就大賺。但三星的中獎難度也高出許多。
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">範例 3：2 膽 5 拖三星拖膽</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              2 個膽碼 + 5 個拖碼，跑三星拖膽。組數 = C(5, 1) = 5 注。<br />
              總成本 = 5 × 50 = <strong className="text-white">250 元</strong>。<br />
              單注獎金（1 賠 580）= 50 × 580 = 29,000 元。<br />
              若中 1 注盈虧 = 29,000 − 250 = <strong className="text-emerald-400">+28,750 元</strong>。<br />
              損益平衡 = 250 / 29,000 ≈ 0.009 注。成本極低、潛在報酬極高——但前提是 2 個膽碼都必須開出，否則連一注都中不了。
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">成本 vs 期望值對照表</h3>
            <div className="overflow-x-auto mb-3">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left py-2 px-3 text-gray-400 font-semibold">投注類型</th>
                    <th className="text-right py-2 px-3 text-gray-400 font-semibold">注數</th>
                    <th className="text-right py-2 px-3 text-gray-400 font-semibold">總成本</th>
                    <th className="text-right py-2 px-3 text-gray-400 font-semibold">單注獎金</th>
                    <th className="text-right py-2 px-3 text-gray-400 font-semibold">中 1 注盈虧</th>
                  </tr>
                </thead>
                <tbody className="text-gray-400">
                  <tr className="border-b border-gray-800/50">
                    <td className="py-2 px-3">6 號二星天碰</td>
                    <td className="py-2 px-3 text-right">15</td>
                    <td className="py-2 px-3 text-right">$750</td>
                    <td className="py-2 px-3 text-right">$2,650</td>
                    <td className="py-2 px-3 text-right text-emerald-400">+$1,900</td>
                  </tr>
                  <tr className="border-b border-gray-800/50">
                    <td className="py-2 px-3">10 號二星天碰</td>
                    <td className="py-2 px-3 text-right">45</td>
                    <td className="py-2 px-3 text-right">$2,250</td>
                    <td className="py-2 px-3 text-right">$2,650</td>
                    <td className="py-2 px-3 text-right text-emerald-400">+$400</td>
                  </tr>
                  <tr className="border-b border-gray-800/50">
                    <td className="py-2 px-3">8 號三星天碰</td>
                    <td className="py-2 px-3 text-right">56</td>
                    <td className="py-2 px-3 text-right">$2,800</td>
                    <td className="py-2 px-3 text-right">$29,000</td>
                    <td className="py-2 px-3 text-right text-emerald-400">+$26,200</td>
                  </tr>
                  <tr className="border-b border-gray-800/50">
                    <td className="py-2 px-3">10 號三星天碰</td>
                    <td className="py-2 px-3 text-right">120</td>
                    <td className="py-2 px-3 text-right">$6,000</td>
                    <td className="py-2 px-3 text-right">$29,000</td>
                    <td className="py-2 px-3 text-right text-emerald-400">+$23,000</td>
                  </tr>
                  <tr className="border-b border-gray-800/50">
                    <td className="py-2 px-3">8 號四星天碰</td>
                    <td className="py-2 px-3 text-right">70</td>
                    <td className="py-2 px-3 text-right">$3,500</td>
                    <td className="py-2 px-3 text-right">$375,000</td>
                    <td className="py-2 px-3 text-right text-emerald-400">+$371,500</td>
                  </tr>
                  <tr className="border-b border-gray-800/50">
                    <td className="py-2 px-3">1 膽 5 拖二星立柱</td>
                    <td className="py-2 px-3 text-right">5</td>
                    <td className="py-2 px-3 text-right">$250</td>
                    <td className="py-2 px-3 text-right">$2,650</td>
                    <td className="py-2 px-3 text-right text-emerald-400">+$2,400</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3">2 膽 5 拖三星拖膽</td>
                    <td className="py-2 px-3 text-right">5</td>
                    <td className="py-2 px-3 text-right">$250</td>
                    <td className="py-2 px-3 text-right">$29,000</td>
                    <td className="py-2 px-3 text-right text-emerald-400">+$28,750</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              從表中可以觀察到幾個規律：<strong className="text-white">號碼選越多，注數越多，成本越高，但單注中獎後的淨利潤越低</strong>。三星和四星的賠率倍數遠高於二星，所以即使注數多，中 1 注的報酬依然遠超成本。但請記住：高賠率也意味著低中獎率，組合數學不會改變每一注的獨立中獎概率。
            </p>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              想精算你自己的投注方案？直接使用上方的 <Link to="/tools/539-combo-calculator" className="text-[#D4AF37] underline">539連碰計算機</Link> 輸入號碼，即時得到組數、成本和損益平衡。也可以搭配 <Link to="/tools/539-payout-calculator" className="text-[#D4AF37] underline">539 獎金計算器</Link> 做更細緻的獎金分析。
            </p>

            {/* ===== 第四章：常見錯誤與迷思 ===== */}
            <h2 className="text-xl font-bold text-white mt-10 mb-4">539 連碰常見錯誤與迷思</h2>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              連碰看似「穩賺不賠」，但其實暗藏許多認知陷阱。以下是五個最常見的迷思，每一個都可能讓你做出錯誤的投注決策。
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">迷思 1：選越多號碼，中獎率越高</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              <strong className="text-white">真相</strong>：選更多號碼確實增加了「至少有一注中獎」的覆蓋率，但每一注的中獎概率並沒有改變。而且組合數呈指數增長——選 15 號跑二星天碰 = C(15, 2) = 105 注，成本 5,250 元。單注獎金 2,650 元，你需要中至少 2 注才回本。期望值（EV）始終是負的：無論你選幾個號碼，每注的 EV 不變。連碰改變的是覆蓋範圍和成本結構，不是數學期望值。
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">迷思 2：連碰一定比單注划算</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              <strong className="text-white">真相</strong>：如果你對號碼有明確判斷（例如只看好 5 個號碼中的某 2 個），單注下注成本 50 元，中獎得 2,650 元，投入報酬比 = 53 倍。而 5 號連碰 C(5, 2) = 10 注 × 50 元 = 500 元，中 1 注只淨賺 2,150 元。連碰適合「不確定哪幾個會開」的情境，如果你的判斷精準，單注反而更有效率。
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">迷思 3：天碰一定比地碰好</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              <strong className="text-white">真相</strong>：天碰覆蓋面廣但成本高，地碰成本低但風險集中。假設你的號碼分析能力一般（跟隨版路或感覺選號），天碰的均勻覆蓋確實更安全。但如果你對某 1-2 個號碼有極高信心（例如追蹤了特定號碼的出球頻率），膽碰的低成本高槓桿反而更適合。關鍵在於你的選號信心分佈是否均勻。
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">迷思 4：歷史熱門號碼更容易出現</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              <strong className="text-white">真相</strong>：每一期 539 開獎都是<strong className="text-white">獨立事件</strong>。上一期開出 07，不代表這一期 07 更容易或更不容易出現。所謂「熱號」和「冷號」只是歷史統計的巧合模式，不具預測能力。數學上，39 個球每期被抽出的概率永遠相同：每個號碼被選中的概率 = 5/39 ≈ 12.82%。不要因為某號碼「已經很久沒開」就加重投注——這是典型的「賭徒謬誤」（Gambler's Fallacy）。
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">迷思 5：5 號連碰是「必中法」</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              <strong className="text-white">真相</strong>：<strong className="text-white">5號連碰</strong>（選 5 個號碼跑二星連碰）= C(5, 2) = 10 注。如果這 5 個號碼中恰好有 2 個或以上被開出，你確實會中至少一注。但問題是：開獎的 5 個球來自 39 個號碼的母體，你選的 5 個號碼全部落空的概率 = C(34, 5) / C(39, 5) ≈ 38.4%。也就是說，<strong className="text-white">將近四成的機率你一注都不會中</strong>。花 500 元（10 注 × 50 元）賭 61.6% 的概率至少中一注二星（獎金 2,650 元），看似不錯，但長期下來 EV 仍是負的。「5號連碰必中法」是一個常被誇大的說法。
            </p>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              更多 539 投注策略的深度分析，可以參考 <Link to="/blog/539-guide" className="text-[#D4AF37] underline">539 新手完整攻略</Link> 和 <Link to="/blog/539-package-betting-guide" className="text-[#D4AF37] underline">539 包牌完整教學</Link>。
            </p>

            {/* ===== 第五章：FAQ ===== */}
            <h2 className="text-xl font-bold text-white mt-10 mb-4">539 連碰常見問題 FAQ</h2>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">Q1：5號連碰怎麼算？</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              5號連碰指的是選 5 個號碼做連碰。以最常見的二星連碰為例：C(5, 2) = 10 注，每注 50 元總成本 500 元。如果改跑三星連碰：C(5, 3) = 10 注，同樣 10 注但賠率從 53 倍跳到 580 倍。使用上方的 <Link to="/tools/539-combo-calculator" className="text-[#D4AF37] underline">539連碰計算機</Link>，選好 5 個號碼後切換玩法，即可看到對應的注數和成本。
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">Q2：天碰和地碰差在哪？</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              天碰（天二碰、天三碰）= 全數組合，所有號碼地位平等，公式 C(N, K)。地碰 = 指定膽碼，膽碼必出現在每注中，從拖碼補齊剩餘星位。天碰注數多、覆蓋廣；地碰注數少、成本低但風險集中在膽碼是否開出。詳細公式與比較請見上方「天地碰計算公式詳解」章節。
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">Q3：連碰最多可以選幾個號碼？</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              理論上 539 有 39 個號碼可選，但實務上受限於成本。選 20 號跑二星天碰 = C(20, 2) = 190 注 × 50 元 = 9,500 元。選 20 號跑三星天碰 = C(20, 3) = 1,140 注 × 50 元 = 57,000 元。大部分彩迷選 6 到 12 個號碼做二星連碰（15 到 66 注），或 5 到 8 個號碼做三星連碰（10 到 56 注），保持總成本在可承受範圍內。本計算機支援任意號碼數量，輸入後即時顯示注數和成本。
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">Q4：天三碰的成本怎麼控制？</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              三星天碰的組數增長速度比二星快得多（三次方 vs 二次方），控制成本的方法有三種：<strong className="text-white">（1）減少選號數量</strong>——從 10 號 → 7 號，注數從 120 注降到 35 注，省 68%。<strong className="text-white">（2）改用拖膽</strong>——鎖定 1-2 個膽碼，大幅減少注數。例如 2 膽 + 5 拖三星拖膽只有 5 注。<strong className="text-white">（3）降低每注金額</strong>——不一定要下 50 元，有些盤口接受 25 元或 10 元一注。用計算機先試算不同方案的總成本，找到你能接受的平衡點。
            </p>

            <h3 className="text-lg font-semibold text-gray-200 mt-6 mb-3">Q5：539 連碰計算機怎麼用？</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              使用步驟非常簡單：<strong className="text-white">（1）選擇玩法</strong>——在頁面頂部的「選擇玩法」區塊點選二星連碰、三星連碰、四星連碰、立柱、天二、天三或拖膽。<strong className="text-white">（2）點選號碼</strong>——在 01-39 的號碼盤上點選你看好的號碼，已選號碼會亮起彩色。如果是膽碼玩法（立柱、拖膽），點選號碼後再點「設膽」指定膽碼。<strong className="text-white">（3）調整金額和賠率</strong>——預設每注 50 元、二星賠率 53 倍，可依你的盤口修改。<strong className="text-white">（4）查看結果</strong>——下方即時顯示組數、總成本、單注獎金、盈虧和損益平衡點。完成後可直接點「用此組計算獎金」跳轉到 <Link to="/tools/539-payout-calculator" className="text-[#D4AF37] underline">獎金計算器</Link> 做進一步分析。
            </p>
          </section>

          {/* ── Disclaimer ── */}
          <div className="flex gap-3 p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-xl">
            <AlertTriangle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <div className="text-xs text-gray-500 leading-relaxed">
              <p className="font-bold text-gray-400 mb-1">免責聲明</p>
              <p>
                本工具僅提供數學組合計算與成本試算，不構成任何投注建議。地下 539 涉及《刑法》賭博罪相關規範，請理性評估法律風險與財務承受能力，量力而為。
                官方台灣彩券依現行法規不得線上投注，需至實體投注站。
              </p>
            </div>
          </div>

          <div className="mt-6 mb-12 flex items-center justify-center gap-2 text-xs text-gray-600">
            <BookOpen className="w-3.5 h-3.5" />
            <Link to="/tools/539" className="hover:text-[#F4C430]">
              返回 539 完整工具集
            </Link>
          </div>
        </main>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Result Card                                                        */
/* ------------------------------------------------------------------ */

function ResultCard({
  label,
  value,
  highlight = false,
  positive,
  fullWidth = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  positive?: boolean;
  fullWidth?: boolean;
}) {
  let valueColor = 'text-white';
  if (highlight) valueColor = 'text-[#F4C430]';
  if (positive === true) valueColor = 'text-emerald-400';
  if (positive === false) valueColor = 'text-red-400';
  return (
    <div
      className={`p-3 rounded-lg bg-[#0a0a0a] border ${
        highlight ? 'border-yellow-500/30' : 'border-gray-800'
      } ${fullWidth ? 'col-span-2' : ''}`}
    >
      <div className="text-[11px] text-gray-500 mb-1">{label}</div>
      <div className={`text-base font-bold ${valueColor}`}>{value}</div>
    </div>
  );
}
